import { GlobalMessages } from '@shared/localization/messages';
import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { useEstimationQueryActions } from '@/api/hooks/estimationQueryActions';
import { Button } from '@/components/atoms/Button';
import { TextFormField } from '@/components/atoms/TextFormField';
import { ModalStateless } from '@/components/ModalStateless';
import { entityFieldSettings } from '@/constants/entityFieldSettings';
import { useEstimation } from '@/context/estimationContext';
import { useToast } from '@/context/toast';
import { useMessages } from '@/hooks/messages';

import { estimationTabMessageDefinitions } from './messages';

export interface EstimationVersionModalProps {
  open: boolean;
  onClose: () => void;
  estimationId: string;
  allowEstimationCreation?: boolean;
}

interface EstimationVersionFormModel {
  versionName: string;
}

const EstimationVersionModal = ({
  open,
  onClose,
  estimationId,
  allowEstimationCreation,
}: EstimationVersionModalProps) => {
  const messages = useMessages(estimationTabMessageDefinitions);
  const globalMessages = useMessages(GlobalMessages);
  const toast = useToast();
  const navigate = useNavigate();

  const {
    actions: { setSavedEstimation },
  } = useEstimation();

  const { useCreateEstimationVersion, useGetEstimation, useUpdateEstimation } =
    useEstimationQueryActions();
  const { data: estimation } = useGetEstimation(estimationId);
  const { mutateAsync: updateEstimation } = useUpdateEstimation();
  const { mutateAsync: createEstimationVersion, isPending } = useCreateEstimationVersion();
  const formModelInitial = useMemo<EstimationVersionFormModel>(
    () => ({
      versionName: '',
    }),
    [],
  );

  const { control, handleSubmit, reset } = useForm<EstimationVersionFormModel>({
    defaultValues: formModelInitial,
  });

  const onSubmit = async (formModel: EstimationVersionFormModel) => {
    if (!estimation) throw new Error('Estimation not loaded');

    if (allowEstimationCreation) {
      try {
        const data = await createEstimationVersion({
          estimationId: estimationId,
          versionData: { versionName: formModel.versionName },
        });
        onClose();
        reset(formModelInitial);
        await navigate({ to: `/estimation/detail/${data.estimation.id}` });
        toast.showToast(messages.versionCreated({ versionName: formModel.versionName }), 'success');
      } catch {
        toast.showToast(globalMessages.unexpectedError(), 'error');
      }
    } else {
      try {
        const data = await updateEstimation({
          estimationId,
          updateProjectWithEstimation: {
            project: { ...estimation.project },
            estimation: { ...estimation.estimation, versionName: formModel.versionName },
          },
        });
        setSavedEstimation(data);
        toast.showToast(messages.versionRenamed(), 'success');
        onClose();
        reset(formModelInitial);
      } catch {
        toast.showToast(globalMessages.unexpectedError(), 'error');
      }
    }
  };

  return (
    <ModalStateless
      size='s'
      title={allowEstimationCreation ? messages.createNewVersion() : messages.renameVersion()}
      buttons={
        <>
          <Button
            variant='secondary'
            outlined
            disabled={isPending}
            onClick={() => {
              reset(formModelInitial);
              onClose();
            }}
          >
            X
          </Button>
          <Button
            data-testid='version-modal-save-button'
            variant='primary'
            onClick={handleSubmit(onSubmit)}
            disabled={isPending}
            isProcessing={isPending}
          >
            {globalMessages.save()}
          </Button>
        </>
      }
      open={open}
      onClose={() => {
        if (!isPending) {
          onClose();
        }
      }}
    >
      <form>
        <div>
          <label htmlFor='versionName'>{messages.versionName()}</label>
          <TextFormField
            control={control}
            fieldPath='versionName'
            rules={{
              required: true,
              maxLength: entityFieldSettings.estimationEntity.versionMaxLength,
            }}
          />
        </div>
      </form>
    </ModalStateless>
  );
};

export default EstimationVersionModal;
